import {
    ActionType,
    ProTable,
    ProColumns
} from '@ant-design/pro-components'
import { Button, Form, Input, Select, message, DatePicker } from 'antd'
import { useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import dayjs from 'dayjs'
import { useDebounceFn } from 'ahooks'
import searchIcon from '@/assets/images/search-icon.jpg'
import styles from './index.module.scss'
import { getSessionListApi } from '@/service/chatHistory/api/getSessionList'
import type { SessionItem } from '@/service/chatHistory/types/getSessionList'
import { getChannelListApi } from '@/service/channel/api/getChannelList'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'
import { useNavigate } from 'react-router-dom'
import whatsappIcon from '@/assets/images/whatsapp.png'
import telegramIcon from '@/assets/images/telegram.png'
import livechatIcon from '@/assets/images/livechat.png'
import lineIcon from '@/assets/images/line.png'

const channelOptions = [
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={whatsappIcon} style={{ width: 20, height: 20 }} />WhatsApp</span>, value: 'WhatsApp' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={whatsappIcon} style={{ width: 20, height: 20 }} />WhatsApp Personal</span>, value: 'WhatsAppPersonal' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={telegramIcon} style={{ width: 20, height: 20 }} />Telegram</span>, value: 'Telegram' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={livechatIcon} style={{ width: 20, height: 20 }} />WebWidget</span>, value: 'WebWidget' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={lineIcon} style={{ width: 20, height: 20 }} />Line</span>, value: 'Line' },
]

const closedTypeColorMap: Record<string, { color: string; bg: string }> = {
    agent: { color: '#51CF66', bg: 'rgba(81, 207, 102, 0.12)' },
    error: { color: '#FF4757', bg: 'rgba(255, 71, 87, 0.12)' },
    timeout: { color: '#FF4757', bg: 'rgba(255, 71, 87, 0.12)' },
    user: { color: '#25D1F7', bg: 'rgba(37, 209, 247, 0.12)' },
    robot: { color: '#a78bfa', bg: 'rgba(139, 92, 246, 0.12)' },
}

const countryNameMap: Record<string, string> = {
    'United States': 'United States of America',
    'CN': 'China',
    // 后续如果有其他需要映射的国家，在这里补充即可
}


const PAGE_SIZE = 10

const ChatDetail = () => {
    const actionRef = useRef<ActionType>()
    const navigate = useNavigate()
    const [form] = Form.useForm()
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)
    const [searchVersion, setSearchVersion] = useState(0)
    const [channelIdList, setChannelIdList] = useState<ChannelItemRes[]>([])

    const handleChannelChange = useCallback((channel: string | undefined) => {
        form.setFieldValue('channel_id', undefined)
        setChannelIdList([])
        if (!channel || !companyName) return
        getChannelListApi({ company: companyName, channel }).then((res) => {
            if (res.code === 0) {
                setChannelIdList(res.data.list)
            }
        })
    }, [companyName, form])

    const doSearch = useCallback(() => {
        setSearchVersion(v => v + 1)
    }, [])

    const { run: handleAutoSearch } = useDebounceFn(doSearch, { wait: 500 })

    const columns: ProColumns<SessionItem>[] = [
        {
            title: 'Nickname',
            dataIndex: 'nickname',
            ellipsis: true,
            minWidth: 100,
            render: (_, record) => record.nickname || '-',
        },
        {
            title: 'User ID',
            dataIndex: 'user_id',
            ellipsis: true,
            minWidth: 160,
            render: (_, record) => (
                <span className={styles['ellipsis-text']}>{record.user_id || '-'}</span>
            ),
        },
        {
            title: 'Call ID',
            dataIndex: 'call_id',
            ellipsis: true,
            minWidth: 160,
            render: (_, record) => (
                <span className={styles['ellipsis-text']}>{record.call_id || '-'}</span>
            ),
        },
        {
            title: 'Channel',
            dataIndex: 'channel',
            ellipsis: true,
            minWidth: 130,
            render: (_, record) => {
                const iconMap: Record<string, string> = {
                    WhatsApp: whatsappIcon,
                    WhatsAppPersonal: whatsappIcon,
                    Telegram: telegramIcon,
                    WebWidget: livechatIcon,
                    Line: lineIcon,
                }
                const icon = iconMap[record.channel]
                return record.channel ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {icon && <img src={icon} style={{ width: 20, height: 20 }} />}
                        {record.channel}
                    </span>
                ) : '-'
            },
        },
        {
            title: 'Channel ID',
            dataIndex: 'channel_id',
            ellipsis: true,
            minWidth: 160,
            render: (_, record) => (
                <span className={styles['ellipsis-text']}>{record.channel_id || '-'}</span>
            ),
        },
        {
            title: 'Country',
            dataIndex: 'country',
            minWidth: 100,
            render: (_, record) => {
                const country = record.country
                if (!country) return '-'
                return countryNameMap[country] || country
            }
        },
        {
            title: 'Language',
            dataIndex: 'language',
            minWidth: 100,
            render: (_, record) =>
                record.language
                    ? record.language.charAt(0).toUpperCase() + record.language.slice(1)
                    : '-',
        },
        {
            title: 'Closed Type',
            dataIndex: 'closed_type',
            minWidth: 100,
            render: (_, record) => {
                const type = record.closed_type
                if (!type) return '-'
                const colorCfg = closedTypeColorMap[type]
                return (
                    <span style={{
                        color: colorCfg?.color || '#717787',
                        background: colorCfg?.bg || 'rgba(113, 119, 135, 0.12)',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: 12,
                    }}>
                        {type}
                    </span>
                )
            },
        },
        {
            title: 'Agent ID',
            dataIndex: 'agent_id',
            minWidth: 130,
            render: (_, record) => record.agent_id || '-',
        },
        {
            title: 'Closed Time',
            dataIndex: 'closed_time',
            minWidth: 180,
            render: (_, record) =>
                record.closed_time
                    ? moment(record.closed_time).format('YYYY/MM/DD HH:mm:ss')
                    : '-',
        },
    ]

    return (
        <div>
            <div className={styles.searchWrapper}>
                <Form
                    form={form}
                    layout="inline"
                    className={styles.searchForm}
                    onValuesChange={handleAutoSearch}
                    initialValues={{
                        dateRange: [dayjs().subtract(7, 'day'), dayjs()],
                    }}
                >
                    <Form.Item name="dateRange">
                        <DatePicker.RangePicker
                            format="YYYY-MM-DD"
                            allowClear={false}
                            className={styles.dateRangePicker}
                            popupClassName={styles.dateRangePopup}
                        />
                    </Form.Item>
                    <Form.Item name="nickname">
                        <Input
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            placeholder="Search Nickname"
                            allowClear
                            autoComplete='off'
                            className={styles.searchInput}
                        />
                    </Form.Item>
                    <Form.Item name="user_id">
                        <Input
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            placeholder="Search User ID"
                            allowClear
                            className={styles.searchInput}
                        />
                    </Form.Item>
                    <Form.Item name="call_id">
                        <Input
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            placeholder="Search Call ID"
                            allowClear
                            autoComplete='off'
                            className={styles.searchInput}
                        />
                    </Form.Item>
                    <Form.Item name="channel">
                        <Select
                            options={channelOptions}
                            placeholder="Select Channel"
                            allowClear
                            className={styles.searchInput}
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            onChange={handleChannelChange}
                        />
                    </Form.Item>
                    <Form.Item name="channel_id">
                        <Select
                            mode="multiple"
                            placeholder="Select Channel ID"
                            allowClear
                            maxTagCount="responsive"
                            className={styles.searchInput}
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            options={channelIdList.map((item) => ({
                                label: item.channel_id,
                                value: item.channel_id,
                            }))}
                            notFoundContent={null}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" style={{ gap: 2 }} icon={<img src={searchIcon} style={{ width: 14, height: 14 }} />} onClick={doSearch}>Search</Button>
                    </Form.Item>
                </Form>
            </div>

            <ProTable<SessionItem>
                actionRef={actionRef}
                className="chat-detail-full-table"
                rowClassName="cursor-pointer"
                rowKey="id"
                columns={columns}
                onRow={(record) => ({
                    onClick: () => {
                        navigate(`/chat-detail/session/${record.id}`)
                    },
                })}
                params={{ companyName, _v: searchVersion }}
                search={false}
                request={async (params) => {
                    try {
                        if (!companyName) {
                            return { success: true, total: 0, data: [] }
                        }
                        const values = form.getFieldsValue()
                        const [startDate, endDate] = values.dateRange || []
                        const res = await getSessionListApi({
                            company: companyName,
                            channel: values.channel || undefined,
                            channel_id: values.channel_id?.length ? values.channel_id : undefined,
                            user_id: values.user_id || undefined,
                            call_id: values.call_id || undefined,
                            nickname: values.nickname || undefined,
                            start_date: startDate ? startDate.format('YYYY-MM-DD') + ' 00:00:00' : undefined,
                            end_date: endDate ? endDate.format('YYYY-MM-DD') + ' 23:59:59' : undefined,
                            page: params.current as number,
                            size: params.pageSize as number,
                        })
                        if (res.code === 0) {
                            return { success: true, total: res.data.total, data: res.data.list }
                        }
                        return { success: false, total: 0, data: [] }
                    } catch {
                        message.error('Failed to fetch session list')
                        return { success: false, total: 0, data: [] }
                    }
                }}
                pagination={{
                    defaultPageSize: PAGE_SIZE,
                    showSizeChanger: false,
                }}
                options={false}
            />
        </div>
    )
}

export default ChatDetail